import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonText } from '@ionic/angular/standalone';
import { interval, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { TranslateModule } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { InitTime, UpdateTime } from 'projects/tools/src/lib/store/puzzle-time-record.actions';
import { PuzzleTimeRecordState } from '../../store/puzzle-time-record.state';
import { PuzzleTimeRecordModel } from '../../store/puzzle-time-record.model';
import { PuzzleGameState } from '../../store/puzzle-game.state';
import { PuzzleGameModel } from '../../store/puzzle-game.model';
import { PuzzleGameStateModel } from '../../store/puzzle-game-state.model';
import { PuzzleFrameworkService } from '../../puzzle-framework-module/puzzle-framework.service';
import { LeaderboardService } from 'projects/tools/src/lib/shared-module/leaderboard/leaderboard.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { TooltipModule } from 'ng2-tooltip-directive-major-angular-updates';

@Component({
  selector: 'lib-app-puzzle-timer',
  templateUrl: './puzzle-timer.component.html',
  styleUrls: ['./puzzle-timer.component.scss'],
  standalone: true,
  imports: [CommonModule, IonText, TranslateModule, TooltipModule],
})
export class PuzzleTimerComponent implements OnInit, OnDestroy {
  timeString: string;

  private puzzleTimeRecordSubscription: Subscription;
  private timerSubscription: Subscription;
  private storeSubscription: Subscription;
  private puzzleID = '';

  private dialogOpen = false;

  private running = false;
  private puzzleGameSubscription: Subscription;

  private gameFinished = true;
  private gamePaused = true;

  constructor(
    public device: DeviceDetectorService,
    private translate: TranslateService,
    private store: Store,
    private cd: ChangeDetectorRef,
    private framework: PuzzleFrameworkService,
    private el: ElementRef,
    private leaderboard: LeaderboardService
  ) {}

  ngOnInit() {
    this.store
      .select((state) => state.puzzleGameState)
      .subscribe((puzzleGameState: PuzzleGameStateModel) => {
        this.gamePaused = puzzleGameState.paused !== 0;
        this.updateRunning();
      });

    this.storeSubscription = this.store
      .select((state) => state.puzzleID)
      .subscribe((puzzleID) => {
        if (puzzleID === '' || puzzleID === undefined) {
          return;
        }

        // Init time as soon as the puzzleId changes
        this.store.dispatch(new InitTime({ puzzleID }));

        if (this.puzzleTimeRecordSubscription) {
          this.puzzleTimeRecordSubscription.unsubscribe();
        }

        this.puzzleID = puzzleID;

        this.stopTimer();
        this.startTimer();

        this.puzzleTimeRecordSubscription = this.store
          .select(PuzzleTimeRecordState.getTimeRecordByID)
          .pipe(map((filterFn) => filterFn(puzzleID)))
          .subscribe((puzzleTimeRecord: PuzzleTimeRecordModel) => {
            if (!puzzleTimeRecord) {
              return;
            }
            this.timeString = puzzleTimeRecord.timeRecord.timeString;

            switch (this.timeString.length) {
              case 4:
              case 5:
              case 6:
                this.el.nativeElement.style.setProperty('--timer-value-font-size', '1.5em');
                break;
              case 7:
                this.el.nativeElement.style.setProperty('--timer-value-font-size', '1.2em');
                break;
              case 8:
                this.el.nativeElement.style.setProperty('--timer-value-font-size', '1.06em');
                break;
              default:
                this.el.nativeElement.style.setProperty('--timer-value-font-size', '0.8em');
                break;
            }

            if (!this.cd[`destroyed`]) {
              this.cd.detectChanges();
            }
          });

        this.puzzleGameSubscription = this.store
          .select(PuzzleGameState.getByID(puzzleID))
          .subscribe((puzzleGame: PuzzleGameModel) => {
            if (!puzzleGame) {
              return;
            }
            this.gameFinished = puzzleGame.finished;
            this.updateRunning();
          });
      });
  }

  private updateRunning() {
    const running = !this.gamePaused && !this.gameFinished;
    if (this.running !== running) {
      this.running = running;
      if (this.running) {
        this.store.dispatch(new InitTime({ puzzleID: this.puzzleID }));
      }
    }
  }

  async timerClicked() {
    if (await this.leaderboard.willDisableLeaderboardRulesDialog('pause')) {
      this.framework.openPauseDialog();
    }
  }

  ngOnDestroy(): void {
    this.stopTimer();
    if (this.puzzleTimeRecordSubscription) {
      this.puzzleTimeRecordSubscription.unsubscribe();
    }
    if (this.puzzleGameSubscription) {
      this.puzzleGameSubscription.unsubscribe();
    }
  }

  startTimer() {
    this.timerSubscription = interval(500).subscribe((counter) => {
      if (!this.running) {
        return;
      }
      this.store.dispatch(new UpdateTime({ puzzleID: this.puzzleID }));
    });
  }

  stopTimer() {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
    if (this.storeSubscription) {
      this.storeSubscription.unsubscribe();
    }
    if (this.puzzleTimeRecordSubscription) {
      this.puzzleTimeRecordSubscription.unsubscribe();
    }
  }
}
